import type {TransitionAsyncState} from '@yandex-delivery/blocks'
import {useTransition} from '@yandex-delivery/blocks'
import type {attachYmaps3React} from '@yandex-delivery/blocks/map/attachYmaps3React'
import {loadYmapsApi} from '@yandex-delivery/blocks/map/loadYmaps'
import type {PropsWithChildren} from 'react'
import React, {useContext} from 'react'

import type {MapRef, Maps, ShowErrorProps} from '../model'
import {ShowError} from '../ui'

export type MapsContextLoadedValue = {
  loaded: true
  ymaps3: Maps
  components: Awaited<ReturnType<typeof attachYmaps3React>>
  lang: string
}

type MapsContextValue = TransitionAsyncState<MapsContextLoadedValue, unknown> & {
  mapRef?: MapRef | null
  setMapRef?: (ref?: MapRef | null) => void
}

const MapsContext = React.createContext<MapsContextValue>({isPending: true})

const LOAD_MAPS_API_KEY = 'LOAD_MAPS_API_KEY'

export const useMapContext = () => useContext(MapsContext)

export const useMapContextValue = () => {
  const {payload: value} = useMapContext()

  return value
}

type MapsContextProviderProps = PropsWithChildren<
  {
    lang: string
    brand: 'yango' | 'yandex'
    mapRef: MapRef | null | undefined
    setMapRef: (ref: MapRef | null | undefined) => void
  } & ShowErrorProps
>

export const MapsContextProvider: React.FC<MapsContextProviderProps> = ({
  errorMessage,
  children,
  lang,
  brand,
  mapRef,
  setMapRef,
}) => {
  const [contextValue] = useTransition(() => loadYmapsApi({lang, brand}), {key: LOAD_MAPS_API_KEY})

  if (contextValue.error) {
    return <ShowError errorMessage={errorMessage} />
  }

  return <MapsContext.Provider value={{...contextValue, mapRef, setMapRef}}>{children}</MapsContext.Provider>
}
